import { create } from 'zustand'
import Keycloak, { type KeycloakTokenParsed } from 'keycloak-js'
import { env } from '@/shared/lib/env'
import { useToastStore } from '@/shared/store/toastStore'
import { getDomain } from "../../../tools/domain";


const domain = getDomain();
const keycloakConfig = {
	url: env.VITE_KEYCLOAK_URL ?? `https://auth.${domain}`,
	realm: env.VITE_KEYCLOAK_REALM,
	clientId: env.VITE_KEYCLOAK_CLIENT_ID,
};

export interface UserProfile {
    name:  string
    email: string
    sub:   string
}

export interface AuthState {
    keycloak: Keycloak | null
    isAuthenticated: boolean
    isLoading: boolean
    initError: string | null
    user: KeycloakTokenParsed | null
    userProfile: UserProfile | null
    token: string | null
    roles: string[]
    isInitialized: boolean
}

export interface AuthActions {
    initializeAuth: () => Promise<void>
    login: () => void
    logout: () => void
    getToken: () => string | null
    hasRole: (role: string) => boolean
    hasAnyRole: (...roles: string[]) => boolean
}

export type AuthStore = AuthState & AuthActions

const extractUserProfile = (tokenParsed: KeycloakTokenParsed | undefined): UserProfile | null => {
    if (!tokenParsed) return null
    return {
        name:  (tokenParsed.name  as string | undefined) ?? '',
        email: (tokenParsed.email as string | undefined) ?? '',
        sub:   tokenParsed.sub ?? '',
    }
}

// Realm roles are emitted into a top-level `roles` claim by the realm's
// "app realm roles" protocol mapper (oidc-usermodel-realm-role-mapper,
// claim.name="roles" — see docs/cloud-app-realm*.json), which is the same claim
// the backend reads (app/keycloak.py::_map_user). This realm does NOT populate
// the standard `realm_access.roles`, so we read the top-level claim first and
// keep `realm_access.roles` only as a defensive fallback. Every authenticated
// user has at least "user" (a default realm role); admins additionally have
// "admin".
const extractRoles = (tokenParsed: KeycloakTokenParsed | undefined): string[] => {
    if (!tokenParsed) return []
    const topLevel = (tokenParsed as { roles?: unknown }).roles
    if (Array.isArray(topLevel)) return topLevel.filter((r): r is string => typeof r === 'string')
    return tokenParsed.realm_access?.roles ?? []
}

// const keycloakConfig: KeycloakConfig = {
//     url:      env.VITE_KEYCLOAK_URL,
//     realm:    env.VITE_KEYCLOAK_REALM,
//     clientId: env.VITE_KEYCLOAK_CLIENT_ID,
// }

// Global singleton to prevent multiple instances (needs to be defined outside the store!!)
let keycloakInstance: Keycloak | null = null
let isInitializing = false
let tokenRefreshInterval: ReturnType<typeof setInterval> | null = null

export const useAuthStore = create<AuthStore>((set, get) => ({
    keycloak: null,
    isAuthenticated: false,
    isLoading: true,
    initError: null,
    user: null,
    userProfile: null,
    token: null,
    roles: [],
    isInitialized: false,

    initializeAuth: async () => {
        const state = get()

        // Prevent multiple initializations (React.StrictMode protection)
        if (state.isInitialized || isInitializing) {
            return
        }

        isInitializing = true

        try {
            if (!keycloakInstance) {
                keycloakInstance = new Keycloak(keycloakConfig)
            }

            const authenticated = await keycloakInstance.init({
                onLoad: 'login-required',
                checkLoginIframe: false,
                pkceMethod: 'S256',
                enableLogging: import.meta.env.DEV,
            })

            set({
                keycloak: keycloakInstance,
                isAuthenticated: authenticated,
                isLoading: false,
                isInitialized: true,
                user: authenticated ? (keycloakInstance.tokenParsed ?? null) : null,
                userProfile: authenticated ? extractUserProfile(keycloakInstance.tokenParsed) : null,
                token: authenticated ? (keycloakInstance.token ?? null) : null,
                roles: authenticated ? extractRoles(keycloakInstance.tokenParsed) : [],
            })

            // Set up token refresh
            if (authenticated && !tokenRefreshInterval) {
                tokenRefreshInterval = setInterval(() => {
                    keycloakInstance!.updateToken(70).then((refreshed) => {
                        if (refreshed) {
                            set({
                                token: keycloakInstance!.token ?? null,
                                user: keycloakInstance!.tokenParsed ?? null,
                                userProfile: extractUserProfile(keycloakInstance!.tokenParsed),
                                roles: extractRoles(keycloakInstance!.tokenParsed),
                            })
                        }
                    }).catch(() => {
                        console.error('Failed to refresh token')
                        useToastStore.getState().show('Your session has expired. Please log in again.', 'warning')
                        get().logout()
                    })
                }, 60000)
            }
        } catch (error) {
            console.error('Failed to initialize Keycloak:', error)
            const message = error instanceof Error ? error.message : 'Authentication service unavailable'
            set({ isLoading: false, isAuthenticated: false, isInitialized: true, initError: message })
        } finally {
            isInitializing = false
        }
    },

    login: () => {
        const { keycloak } = get()
        if (keycloak) keycloak.login()
    },

    logout: () => {
        const { keycloak } = get()
        if (keycloak) {
            if (tokenRefreshInterval !== null) {
                clearInterval(tokenRefreshInterval)
                tokenRefreshInterval = null
            }
            set({ isAuthenticated: false, user: null, userProfile: null, token: null, roles: [] })
            keycloak.logout()
        }
    },

    getToken: () => get().token,

    hasRole: (role: string) => get().roles.includes(role),

    hasAnyRole: (...roles: string[]) => {
        const owned = get().roles
        return roles.some((role) => owned.includes(role))
    },
}))
